<template>
  <div class="ui-logs">
    <div v-if="isLoading" class="ui-logs__skeleton">
      <div v-for="n in 5" :key="'sk' + n" class="ui-logs__skeleton-row">
        <span class="ui-sk ui-sk--sm"></span>
        <span class="ui-sk"></span>
      </div>
    </div>

    <div v-else-if="!logs.length" class="ui-empty">
      <i class="fa fa-history"></i>
      <div class="ui-empty__title">Логов нет</div>
      <div class="ui-empty__text">Действия с аккаунтом появятся здесь</div>
    </div>

    <template v-else>
      <div class="ui-table-wrap">
        <table class="ui-table">
          <thead>
          <tr>
            <th>Дата</th>
            <th>Тип</th>
            <th>Описание</th>
            <th>Кто</th>
            <th></th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="log in logs" :key="'log' + log.id">
            <td>
              <div class="ui-time">
                <span>{{ formatDateTime(log.created_at) }}</span>
                <small>{{ formatRelative(log.created_at) }}</small>
              </div>
            </td>
            <td>
              <span class="ui-tag">{{ logCode(log) || '—' }}</span>
            </td>
            <td>
              <div class="ui-log">{{ logDescription(log) || '—' }}</div>
            </td>
            <td>{{ (log.author && log.author.username) || '—' }}</td>
            <td class="ui-logs__id">
              <span class="ui-dim">#{{ log.id }}</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="ui-foot">
        <b-pagination class="ui-pagination my-0"
                      v-model="userLogsCurrentPage"
                      :total-rows="totalPagesOfUserLogs"
                      :per-page="10"
                      :limit="7"
                      @change="handleLogsPageChange"/>
      </div>
    </template>
  </div>
</template>

<script>
import api from "@/api/api";
import {getLogData} from "@/common/userLogs";
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";

export default {
  name: 'UserLogs',
  mixins: [PaginationHelperMixin],
  data() {
    return {
      logs: [],
      isLoading: true,
      logsPageKey: 'userLogsPageNumber',
      totalPagesOfUserLogs: 1,
      userLogsCurrentPage: 1,
    }
  },
  created() {
    this.recordPageToStorage(this.userLogsCurrentPage, this.logsPageKey);
    this.loadLogs(this.userLogsCurrentPage).then((pagination) => {
      this.totalPagesOfUserLogs = (pagination && pagination.total) || 0;
    });
  },
  methods: {
    async loadLogs(pageNumber) {
      this.isLoading = true;
      try {
        const response = await api.users.userLogs({userId: this.$route.params.id, page: pageNumber});
        this.logs = (response.data && response.data.user_logs) || [];
        return response.data && response.data.pagination;
      } catch (error) {
        this.logs = [];
        return null;
      } finally {
        this.isLoading = false;
      }
    },

    handleLogsPageChange(pageNumber) {
      // b-pagination отдаёт input (v-model) только на следующем тике, а isLoading
      // успевает размонтировать её раньше — поэтому номер страницы ставим вручную.
      this.userLogsCurrentPage = pageNumber;
      this.recordPageToStorage(pageNumber, this.logsPageKey);
      return this.loadLogs(pageNumber);
    },

    logCode(log) {
      return log.type && log.type.code;
    },

    /** Описание собирается в common/userLogs по коду события. */
    logDescription(log) {
      const code = this.logCode(log);
      if (!code) return '';
      return getLogData({item: log})[code] || '';
    },

    formatDateTime(value) {
      if (!value) return '—';
      const date = new Date(String(value).replace(' ', 'T'));
      if (isNaN(date.getTime())) return '—';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    formatRelative(value) {
      if (!value) return '';
      const date = new Date(String(value).replace(' ', 'T'));
      if (isNaN(date.getTime())) return '';
      const minutes = Math.round((Date.now() - date.getTime()) / 60000);
      if (minutes < 1) return 'только что';
      if (minutes < 60) return `${minutes} мин. назад`;
      const hours = Math.round(minutes / 60);
      if (hours < 24) return `${hours} ч. назад`;
      const days = Math.round(hours / 24);
      if (days < 30) return `${days} дн. назад`;
      return `${Math.round(days / 30)} мес. назад`;
    },
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/tournament-form-tokens";

.ui-logs {
  @include tf-tokens-light;

  min-width: 0;
}

body.dark-only .ui-logs {
  @include tf-tokens-dark;
}

.ui-table-wrap { overflow-x: auto; }

.ui-table {
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 11px 16px;
    border-bottom: 1px solid var(--tf-border);
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: var(--tf-muted);
    text-align: left;
    white-space: nowrap;
  }

  td {
    padding: 11px 16px;
    border-bottom: 1px solid var(--tf-border);
    font-size: 13px;
    color: var(--tf-text);
    vertical-align: top;
  }

  tbody tr:last-child td { border-bottom: 0; }

  tbody tr:hover td { background: var(--tf-soft); }
}

.ui-time {
  display: flex;
  flex-direction: column;
  white-space: nowrap;

  small { font-size: 11px; color: var(--tf-muted); }
}

.ui-tag {
  display: inline-flex;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--tf-primary-soft);
  color: var(--tf-primary);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

/* описание приходит многострочным — раньше его рисовали <pre> на белом фоне */
.ui-log {
  max-width: 620px;
  font-size: 12.5px;
  line-height: 1.55;
  white-space: pre-line;
}

.ui-logs__id { text-align: right; white-space: nowrap; }

.ui-dim { font-size: 11.5px; color: var(--tf-muted); }

.ui-foot {
  display: flex;
  justify-content: flex-end;
  padding: 13px 16px 0;
}

.ui-pagination ::v-deep {
  .page-item .page-link {
    border: 0;
    margin: 0 2px;
    min-width: 32px;
    height: 32px;
    line-height: 32px;
    padding: 0;
    text-align: center;
    border-radius: 9px;
    color: var(--tf-muted);
    background: transparent;
    font-size: 13px;
  }

  .page-item .page-link:hover { background: var(--tf-soft-2); color: var(--tf-primary); }

  .page-item.active .page-link { background: var(--tf-primary); color: #fff !important; }

  .page-item.disabled .page-link { opacity: .4; }
}

.ui-empty {
  padding: 34px 16px;
  text-align: center;
  color: var(--tf-muted);

  > i { font-size: 22px; opacity: .6; }

  &__title { margin-top: 10px; font-size: 14px; font-weight: 600; color: var(--tf-text); }

  &__text { margin-top: 2px; font-size: 12.5px; }
}

.ui-logs__skeleton-row {
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  gap: 16px;
  padding: 13px 16px;
  border-bottom: 1px solid var(--tf-border);

  &:last-child { border-bottom: 0; }
}

.ui-sk {
  height: 14px;
  border-radius: 7px;
  background: linear-gradient(90deg, var(--tf-soft) 25%, var(--tf-soft-2) 37%, var(--tf-soft) 63%);
  background-size: 400% 100%;
  animation: ui-shimmer 1.3s ease infinite;

  &--sm { height: 12px; }
}

@keyframes ui-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}
</style>
